import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  FSU_PROGRAM, FSU_PHOTOS, INTL_PARTNERSHIPS, KOBZAR_QUOTES, NEWS_CITATIONS
} from "../../data/presentationData";
import { SW, KobzarQuote, CitationLink, CitationBar } from "./shared";

export function Section2() {
  const [showTimeline, setShowTimeline] = useState(false);
  const [showIntl, setShowIntl] = useState(false);
  return (
    <SW id="section-2" dark>
      <div className="mb-6 stagger-1">
        <span className="text-[#C5A258] text-xs font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>The FSU Path</span>
        <h2 className="text-4xl md:text-5xl font-bold mt-3">Four Hours Away. Three MOUs. The Warmest Pathway.</h2>
        <p className="text-slate-400 mt-4 text-lg max-w-3xl" style={{ fontFamily: 'var(--font-body)' }}>
          Florida State University has built the most active Ukraine academic engagement of any Florida university {'\u2014'} and they're just four hours from NCF. In 2025, the Task Force engaged <span className="text-white font-semibold">30 Ukrainian universities</span>, hosted <span className="text-white font-semibold">21 visitors from Ukraine</span>, and offered <span className="text-white font-semibold">17 educational events</span>. FSU's Student Senate passed a <CitationLink href="https://lsi.fsu.edu/article/student-senate-passes-proclamation-lauding-fsus-ukraine-task-force">formal proclamation</CitationLink> lauding the Task Force's contributions in March 2026.
        </p>
      </div>
      <KobzarQuote quote={KOBZAR_QUOTES[0]} dark />
      <div className="grid grid-cols-4 gap-3 mb-8 stagger-2">
        {FSU_PROGRAM.stats.map((s, i) => (
          <div key={i} className="text-center bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="text-2xl font-bold text-[#C5A258]" style={{ fontFamily: 'var(--font-display)' }}>{s.value}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1" style={{ fontFamily: 'var(--font-body)' }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-3 mb-8 stagger-3">
        {FSU_PHOTOS.map((photo, i) => (
          <div key={i} data-testid={`fsu-photo-${i}`} className="rounded-xl overflow-hidden border border-slate-700/50 bg-slate-800/50">
            <img src={photo.url} alt={photo.alt} className="w-full h-40 object-cover" loading="lazy" />
            <div className="p-3"><p className="text-[11px] text-slate-400 leading-snug" style={{ fontFamily: 'var(--font-body)' }}>{photo.caption}</p></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6 stagger-4">
        {FSU_PROGRAM.keyFigures.map((f, i) => (
          <div key={i} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
            <div className="font-bold text-white text-sm">{f.name}</div>
            <div className="text-xs text-[#C5A258] mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>{f.role}</div>
            <p className="text-slate-400 text-xs mt-2 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{f.detail}</p>
          </div>
        ))}
      </div>
      <button data-testid="toggle-fsu-timeline" onClick={() => setShowTimeline(!showTimeline)}
        className="text-sm text-[#C5A258] hover:text-[#d4b46a] flex items-center gap-1 mb-4 transition stagger-5" style={{ fontFamily: 'var(--font-body)' }}>
        {showTimeline ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {showTimeline ? "Hide" : "Show"} FSU Ukraine Engagement Timeline
      </button>
      {showTimeline && (
        <div className="grid grid-cols-3 gap-3 mb-6 tooltip-enter">
          {FSU_PROGRAM.timeline.map((t, i) => (
            <div key={i} className="bg-[#C5A258]/5 border border-[#C5A258]/20 rounded-xl p-4">
              <div className="text-[#C5A258] font-bold text-sm mb-1" style={{ fontFamily: 'var(--font-display)' }}>{t.year}</div>
              <div className="text-white font-semibold text-sm mb-1">{t.event}</div>
              <p className="text-slate-400 text-xs" style={{ fontFamily: 'var(--font-body)' }}>{t.detail}</p>
            </div>
          ))}
        </div>
      )}
      <button data-testid="toggle-intl-partnerships" onClick={() => setShowIntl(!showIntl)}
        className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1 mb-4 transition stagger-6" style={{ fontFamily: 'var(--font-body)' }}>
        {showIntl ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {showIntl ? "Hide" : "Explore"} Global University-Ukraine Partnership Landscape
      </button>
      {showIntl && (
        <div className="space-y-3 tooltip-enter mb-6">
          <p className="text-slate-500 text-sm mb-4" style={{ fontFamily: 'var(--font-body)' }}>{INTL_PARTNERSHIPS.summary}</p>
          {INTL_PARTNERSHIPS.regions.map((r, i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                <span className="font-bold text-white text-sm">{r.region}</span>
                <span className="text-slate-400 text-xs ml-1" style={{ fontFamily: 'var(--font-body)' }}>{r.headline}</span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{r.detail}</p>
              <div className="mt-2 bg-red-950/20 border border-red-800/20 rounded-lg px-3 py-1.5">
                <p className="text-red-400/80 text-xs font-medium" style={{ fontFamily: 'var(--font-body)' }}>Gap: {r.gap}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="bg-[#C5A258]/10 border border-[#C5A258]/25 rounded-2xl p-6 mt-4">
        <p className="text-[#C5A258]/90 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
          <span className="font-bold text-[#C5A258]">The FSU playbook:</span> Start with faculty exchange (BridgeUSA), build MOUs with key universities, expand through collaboration. NCF follows the same path {'\u2014'} but with a <span className="font-semibold text-white">specific applied data science focus</span> that no institution, including FSU, currently offers for the Diia ecosystem.
        </p>
      </div>
      <CitationBar citations={NEWS_CITATIONS.fsu} dark />
    </SW>
  );
}
